import React, { useState, useCallback } from 'react';
import TenantConfigService from '../../services/TenantConfigService';

/**
 * Division CSV Uploader
 * 
 * Access this page directly at: /admin/upload-divisions
 * Not included in main navigation - admin utility page only
 * 
 * Expected CSV format:
 * code, label, color, order, calendarAssignmentId
 * 
 * Example:
 * B, Teacher B, #007bff, 1, AB
 * C, Teacher C, #28a745, 2, AC
 * AF, Teacher AF, #ffc107, 3, AAF
 */

// Predefined color palette for auto-assignment (200 unique colors)
const COLOR_PALETTE = [
  // Blues
  '#007bff', '#0056b3', '#004085', '#002d5a', '#0d6efd', '#0a58ca', '#084298', '#052c65',
  '#3a7db8', '#2c6aa0', '#1e5788', '#104470', '#6ea8d9', '#5a9bd4', '#468ecf', '#3281ca',
  '#87ceeb', '#6bbfdf', '#4fb0d3', '#33a1c7', '#00bcd4', '#00a0b8', '#00849c', '#006880',
  '#17a2b8', '#138496', '#0f6674', '#0b4852', '#20c997', '#1aa87f', '#148767', '#0e664f',
  
  // Greens
  '#28a745', '#1e7e34', '#155724', '#0d3d1a', '#198754', '#146c43', '#0f5132', '#0a3622',
  '#4caf50', '#3d8b40', '#2e6730', '#1f4320', '#8bc34a', '#7cb342', '#6d9e3a', '#5e8932',
  '#cddc39', '#b8c92d', '#a3b621', '#8ea315', '#009688', '#007a6e', '#005e54', '#00423a',
  '#00796b', '#00695c', '#00594d', '#00493e', '#2e7d32', '#256427', '#1c4b1c', '#133211',
  
  // Yellows/Oranges
  '#ffc107', '#d39e00', '#a67c00', '#795a00', '#ffca2c', '#e6b800', '#cca600', '#b39400',
  '#ff9800', '#e68900', '#cc7a00', '#b36b00', '#fd7e14', '#e56b00', '#cc5f00', '#b35300',
  '#ff5722', '#e64a19', '#d84315', '#bf360c', '#ff7043', '#ff5722', '#f4511e', '#e64a19',
  '#ffb74d', '#ffa726', '#ff9800', '#fb8c00', '#ffcc80', '#ffb74d', '#ffa726', '#ff9800',
  
  // Reds/Pinks
  '#dc3545', '#bd2130', '#921925', '#6d131c', '#f44336', '#e53935', '#d32f2f', '#c62828',
  '#e91e63', '#d81b60', '#c2185b', '#ad1457', '#e83e8c', '#d63384', '#c42d78', '#b2276c',
  '#f06292', '#ec407a', '#e91e63', '#d81b60', '#f48fb1', '#f06292', '#ec407a', '#e91e63',
  '#ff4081', '#f50057', '#c51162', '#a80051', '#ff80ab', '#ff4081', '#f50057', '#c51162',
  
  // Purples
  '#6f42c1', '#5a32a3', '#4e2a84', '#422265', '#9c27b0', '#8e24aa', '#7b1fa2', '#6a1b9a',
  '#673ab7', '#5e35b1', '#512da8', '#4527a0', '#7c4dff', '#651fff', '#6200ea', '#5600d1',
  '#ba68c8', '#ab47bc', '#9c27b0', '#8e24aa', '#ce93d8', '#ba68c8', '#ab47bc', '#9c27b0',
  '#9575cd', '#7e57c2', '#673ab7', '#5e35b1', '#b39ddb', '#9575cd', '#7e57c2', '#673ab7',
  
  // Grays/Neutrals
  '#6c757d', '#5a6268', '#484e53', '#36393d', '#343a40', '#292d32', '#1e2124', '#131516',
  '#607d8b', '#546e7a', '#455a64', '#37474f', '#78909c', '#607d8b', '#546e7a', '#455a64',
  '#9e9e9e', '#8a8a8a', '#767676', '#626262', '#bdbdbd', '#9e9e9e', '#757575', '#616161',
  '#795548', '#6d4c41', '#5d4037', '#4e342e', '#a1887f', '#8d6e63', '#795548', '#6d4c41',
  
  // Additional unique colors
  '#00acc1', '#0097a7', '#00838f', '#006978', '#26c6da', '#00bcd4', '#00acc1', '#0097a7',
  '#66bb6a', '#4caf50', '#43a047', '#388e3c', '#81c784', '#66bb6a', '#4caf50', '#43a047',
  '#ffee58', '#ffeb3b', '#fdd835', '#fbc02d', '#fff59d', '#fff176', '#ffee58', '#ffeb3b',
  '#ff7043', '#ff5722', '#f4511e', '#e64a19', '#ffab91', '#ff8a65', '#ff7043', '#ff5722',
  '#8d6e63', '#795548', '#6d4c41', '#5d4037', '#bcaaa4', '#a1887f', '#8d6e63', '#795548',
  '#78909c', '#607d8b', '#546e7a', '#455a64', '#b0bec5', '#90a4ae', '#78909c', '#607d8b',
  
  // More vibrant options
  '#00e676', '#00c853', '#00b248', '#009d3d', '#69f0ae', '#00e676', '#00c853', '#00b248',
  '#ff6e40', '#ff5722', '#f4511e', '#e64a19', '#ffab91', '#ff8a65', '#ff7043', '#ff6e40',
  '#40c4ff', '#00b0ff', '#0091ea', '#0077c2', '#80d8ff', '#40c4ff', '#00b0ff', '#0091ea',
  '#ea80fc', '#e040fb', '#d500f9', '#aa00ff', '#ea80fc', '#e040fb', '#d500f9', '#aa00ff',
  '#b2ff59', '#76ff03', '#64dd17', '#52c41a', '#ccff90', '#b2ff59', '#76ff03', '#64dd17',
  '#ffd740', '#ffc400', '#ffab00', '#ff9100', '#ffe57f', '#ffd740', '#ffc400', '#ffab00'
];

const DivisionUploader = ({ currentUser }) => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [previewMode, setPreviewMode] = useState(true);
  const [selectedDivisions, setSelectedDivisions] = useState(new Set());
  const [parseError, setParseError] = useState(null);
  const [validationWarnings, setValidationWarnings] = useState([]);

  const tenantId = currentUser?.tenantId;

  // Parse CSV file
  const parseCSV = useCallback((text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) {
      throw new Error('CSV must have a header row and at least one data row');
    }

    // Parse header
    const header = lines[0].split(',').map(h => h.trim().replace(/"/g, '').toLowerCase());
    
    // Find column indices
    const codeIdx = header.findIndex(h => h === 'code' || h === 'division_code' || h === 'id');
    const labelIdx = header.findIndex(h => h === 'label' || h === 'name' || h === 'division_name');
    const colorIdx = header.findIndex(h => h === 'color' || h === 'hex_color');
    const orderIdx = header.findIndex(h => h === 'order' || h === 'sort_order' || h === 'display_order');
    const calAssignIdx = header.findIndex(h => 
      h === 'calendarassignmentid' || 
      h === 'calendar_assignment_id' || 
      h === 'calendar_assignment' || 
      h === 'cal_assignment' ||
      h === 'schedule_id' ||
      h === 'scheduleid'
    );

    if (codeIdx === -1) {
      throw new Error('CSV must have a "code" column (or "division_code" or "id")');
    }

    const warnings = [];
    if (calAssignIdx === -1) {
      warnings.push('No "calendarAssignmentId" column found. Divisions will not be linked to calendar assignments. Teachers may see the default schedule instead of their correct schedule.');
    }

    // Parse data rows
    const divisions = [];
    const seenCalAssignments = new Set();

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue;

      // Handle quoted values with commas
      const values = [];
      let current = '';
      let inQuotes = false;
      for (const char of line) {
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());

      const code = values[codeIdx]?.replace(/"/g, '').trim();
      if (!code) continue;

      // Get label (default to code if not provided)
      const label = labelIdx !== -1 ? values[labelIdx]?.replace(/"/g, '').trim() : '';
      
      // Get color (default from palette if not provided)
      let color = colorIdx !== -1 ? values[colorIdx]?.replace(/"/g, '').trim() : '';
      if (!color || !color.startsWith('#')) {
        color = COLOR_PALETTE[(i - 1) % COLOR_PALETTE.length];
      }
      
      // Get order (default to row number if not provided)
      const orderValue = orderIdx !== -1 ? values[orderIdx]?.replace(/"/g, '').trim() : '';
      const order = orderValue ? parseInt(orderValue, 10) : i;

      // Get calendarAssignmentId
      const calendarAssignmentId = calAssignIdx !== -1 
        ? values[calAssignIdx]?.replace(/"/g, '').trim() || '' 
        : '';

      // Track duplicate calendar assignment IDs
      if (calendarAssignmentId) {
        if (seenCalAssignments.has(calendarAssignmentId)) {
          warnings.push(`Row ${i + 1}: Duplicate calendarAssignmentId "${calendarAssignmentId}" for division "${code}". Each division should have a unique calendar assignment.`);
        }
        seenCalAssignments.add(calendarAssignmentId);
      } else if (calAssignIdx !== -1) {
        warnings.push(`Row ${i + 1}: Division "${code}" has no calendarAssignmentId. It will fall back to the default schedule.`);
      }

      divisions.push({
        code,
        label: label || `Teacher ${code}`,
        color,
        order: isNaN(order) ? i : order,
        calendarAssignmentId
      });
    }

    // Sort by order
    divisions.sort((a, b) => a.order - b.order);

    setValidationWarnings(warnings);
    return divisions;
  }, []);

  // Handle file selection
  const handleFileChange = useCallback((e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setParseError(null);
    setUploadStatus(null);
    setValidationWarnings([]);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const divisions = parseCSV(event.target.result);
        setParsedData(divisions);
        setSelectedDivisions(new Set(divisions.map(d => d.code)));
      } catch (err) {
        setParseError(err.message);
        setParsedData([]);
      }
    };
    reader.readAsText(selectedFile);
  }, [parseCSV]);

  // Toggle division selection
  const toggleDivision = (code) => {
    const newSelected = new Set(selectedDivisions);
    if (newSelected.has(code)) {
      newSelected.delete(code);
    } else {
      newSelected.add(code);
    }
    setSelectedDivisions(newSelected);
  };

  // Select/deselect all
  const toggleAll = () => {
    if (selectedDivisions.size === parsedData.length) {
      setSelectedDivisions(new Set());
    } else {
      setSelectedDivisions(new Set(parsedData.map(d => d.code)));
    }
  };

  // Upload to Firestore
  const handleUpload = async () => {
    if (!tenantId) {
      setUploadStatus({ type: 'error', message: 'No tenant ID found. Please log in again.' });
      return;
    }

    const divisionsToUpload = parsedData.filter(d => selectedDivisions.has(d.code));
    if (divisionsToUpload.length === 0) {
      setUploadStatus({ type: 'error', message: 'No divisions selected for upload.' });
      return;
    }

    // Check for missing calendar assignments
    const missingCalAssign = divisionsToUpload.filter(d => !d.calendarAssignmentId);
    if (missingCalAssign.length > 0) {
      const proceed = window.confirm(
        `${missingCalAssign.length} division(s) have no calendarAssignmentId and will use the default schedule:\n\n` +
        missingCalAssign.slice(0, 10).map(d => `• ${d.code} (${d.label})`).join('\n') +
        (missingCalAssign.length > 10 ? `\n...and ${missingCalAssign.length - 10} more` : '') +
        '\n\nDo you want to continue?'
      );
      if (!proceed) return;
    }

    setUploading(true);
    setUploadStatus(null);

    try {
      // Get existing divisions config
      const existingConfig = await TenantConfigService.getConfig(tenantId, 'divisions', { 
        divisions: [] 
      });
      
      const existingDivisions = existingConfig.divisions || [];

      // Merge - update existing divisions by code, add new ones
      const updatedDivisions = [...existingDivisions];

      for (const division of divisionsToUpload) {
        const existingIndex = updatedDivisions.findIndex(d => d.code === division.code);
        if (existingIndex >= 0) {
          // Preserve any existing fields not in the CSV, but update what we have
          updatedDivisions[existingIndex] = {
            ...updatedDivisions[existingIndex],
            ...division
          };
        } else {
          updatedDivisions.push(division);
        }
      }

      // Re-sort by order
      updatedDivisions.sort((a, b) => a.order - b.order);

      // Save using TenantConfigService
      const success = await TenantConfigService.updateConfig(tenantId, 'divisions', {
        divisions: updatedDivisions
      });

      if (success) {
        const withCalAssign = divisionsToUpload.filter(d => d.calendarAssignmentId).length;
        setUploadStatus({
          type: 'success',
          message: `Successfully uploaded ${divisionsToUpload.length} divisions! ${withCalAssign} linked to calendar assignments.`
        });
      } else {
        throw new Error('Update returned false');
      }

    } catch (err) {
      console.error('Upload error:', err);
      setUploadStatus({
        type: 'error',
        message: `Upload failed: ${err.message}`
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>🏫 Division Uploader</h1>
        <p style={styles.subtitle}>
          Upload CSV file to bulk import divisions (teacher codes) into Relief Roster
        </p>
      </div>

      {/* Instructions */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>📋 CSV Format Requirements</h3>
        <div style={styles.instructions}>
          <p>Your CSV file should have the following columns:</p>
          <code style={styles.codeBlock}>
            code, label, color, order, calendarAssignmentId
          </code>
          <p style={{ marginTop: '12px' }}>
            <strong>Required:</strong> code (e.g., "B", "C", "AF")<br />
            <strong>Recommended:</strong> calendarAssignmentId — links this division to its calendar assignment schedule (e.g., "AB", "AC"). Without this, teachers in the division will see the default schedule.<br />
            <strong>Optional:</strong> label (defaults to "Teacher [code]"), color (auto-assigned if missing), order (defaults to row number)
          </p>
          <div style={styles.importantNote}>
            <strong>⚠️ Important:</strong> The <code>calendarAssignmentId</code> must match a key in your <em>calendar_assignments</em> config. 
            This is how the system knows which schedule (time blocks, periods, rotation patterns) applies to each division. 
            If omitted, teachers will fall back to the default schedule.
          </div>
          <p style={{ marginTop: '12px' }}>
            <strong>Example:</strong>
          </p>
          <code style={styles.codeBlock}>
            code,label,color,order,calendarAssignmentId{'\n'}
            B,Teacher B,#007bff,1,AB{'\n'}
            C,Teacher C,#28a745,2,AC{'\n'}
            AF,Teacher AF,#ffc107,3,AAF
          </code>
        </div>
      </div>

      {/* File Upload */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>📤 Select File</h3>
        <div style={styles.uploadArea}>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            style={styles.fileInput}
            id="csv-upload"
          />
          <label htmlFor="csv-upload" style={styles.uploadLabel}>
            {file ? (
              <>
                <span style={styles.fileName}>✅ {file.name}</span>
                <span style={styles.fileSize}>({(file.size / 1024).toFixed(1)} KB)</span>
              </>
            ) : (
              <>
                <span style={styles.uploadIcon}>📁</span>
                <span>Click to select CSV file or drag and drop</span>
              </>
            )}
          </label>
        </div>

        {parseError && (
          <div style={styles.errorMessage}>
            ❌ {parseError}
          </div>
        )}
      </div>

      {/* Validation Warnings */}
      {validationWarnings.length > 0 && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>⚠️ Validation Warnings</h3>
          <div style={styles.warningList}>
            {validationWarnings.map((warning, idx) => (
              <div key={idx} style={styles.warningItem}>
                {warning}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Preview */}
      {parsedData.length > 0 && (
        <div style={styles.card}>
          <div style={styles.previewHeader}>
            <h3 style={styles.cardTitle}>
              👁️ Preview ({parsedData.length} divisions found)
            </h3>
            <div style={styles.previewActions}>
              <button
                onClick={toggleAll}
                style={styles.selectAllBtn}
              >
                {selectedDivisions.size === parsedData.length ? 'Deselect All' : 'Select All'}
              </button>
              <span style={styles.selectedCount}>
                {selectedDivisions.size} selected
              </span>
            </div>
          </div>

          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Select</th>
                  <th style={styles.th}>Code</th>
                  <th style={styles.th}>Label</th>
                  <th style={styles.th}>Color</th>
                  <th style={styles.th}>Order</th>
                  <th style={styles.th}>Calendar Assignment</th>
                </tr>
              </thead>
              <tbody>
                {parsedData.slice(0, previewMode ? 30 : parsedData.length).map((division) => (
                  <tr key={division.code} style={styles.tr}>
                    <td style={styles.td}>
                      <input
                        type="checkbox"
                        checked={selectedDivisions.has(division.code)}
                        onChange={() => toggleDivision(division.code)}
                        style={styles.checkbox}
                      />
                    </td>
                    <td style={styles.td}>
                      <code style={styles.divisionCode}>{division.code}</code>
                    </td>
                    <td style={styles.td}>{division.label}</td>
                    <td style={styles.td}>
                      <div style={styles.colorCell}>
                        <span 
                          style={{
                            ...styles.colorSwatch,
                            backgroundColor: division.color
                          }}
                        />
                        <code style={styles.colorCode}>{division.color}</code>
                      </div>
                    </td>
                    <td style={styles.td}>{division.order}</td>
                    <td style={styles.td}>
                      {division.calendarAssignmentId ? (
                        <code style={styles.calAssignBadge}>{division.calendarAssignmentId}</code>
                      ) : (
                        <span style={styles.missingCalAssign}>⚠️ Not set</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {parsedData.length > 30 && (
            <button
              onClick={() => setPreviewMode(!previewMode)}
              style={styles.showMoreBtn}
            >
              {previewMode ? `Show all ${parsedData.length} divisions` : 'Show less'}
            </button>
          )}
        </div>
      )}

      {/* Upload Button */}
      {parsedData.length > 0 && (
        <div style={styles.uploadSection}>
          <button
            onClick={handleUpload}
            disabled={uploading || selectedDivisions.size === 0}
            style={{
              ...styles.uploadBtn,
              ...(uploading || selectedDivisions.size === 0 ? styles.uploadBtnDisabled : {})
            }}
          >
            {uploading ? (
              <>⏳ Uploading...</>
            ) : (
              <>🚀 Upload {selectedDivisions.size} Divisions to Firestore</>
            )}
          </button>

          {uploadStatus && (
            <div style={{
              ...styles.statusMessage,
              ...(uploadStatus.type === 'success' ? styles.successMessage : styles.errorMessage)
            }}>
              {uploadStatus.message}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    marginBottom: '24px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a202c',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '16px',
    color: '#718096',
    margin: 0,
  },
  card: {
    background: '#ffffff',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #e2e8f0',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2d3748',
    margin: '0 0 16px 0',
  },
  instructions: {
    fontSize: '14px',
    color: '#4a5568',
    lineHeight: '1.6',
  },
  importantNote: {
    marginTop: '12px',
    padding: '12px 16px',
    background: '#fffbeb',
    border: '1px solid #f6e05e',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#744210',
    lineHeight: '1.5',
  },
  codeBlock: {
    display: 'block',
    background: '#f7fafc',
    padding: '12px 16px',
    borderRadius: '6px',
    fontSize: '12px',
    color: '#2d3748',
    overflowX: 'auto',
    border: '1px solid #e2e8f0',
    whiteSpace: 'pre-wrap',
  },
  uploadArea: {
    position: 'relative',
  },
  fileInput: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    opacity: 0,
    cursor: 'pointer',
  },
  uploadLabel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    border: '2px dashed #cbd5e0',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    color: '#718096',
    fontSize: '14px',
    gap: '8px',
  },
  uploadIcon: {
    fontSize: '32px',
  },
  fileName: {
    color: '#2b6cb0',
    fontWeight: '600',
  },
  fileSize: {
    color: '#a0aec0',
    fontSize: '12px',
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
  },
  previewActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  selectAllBtn: {
    padding: '6px 12px',
    fontSize: '13px',
    background: '#edf2f7',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#4a5568',
  },
  selectedCount: {
    fontSize: '13px',
    color: '#718096',
  },
  warningList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  warningItem: {
    padding: '8px 12px',
    background: '#fffbeb',
    border: '1px solid #f6e05e',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#744210',
  },
  tableContainer: {
    overflowX: 'auto',
    marginTop: '16px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  th: {
    textAlign: 'left',
    padding: '12px',
    borderBottom: '2px solid #e2e8f0',
    color: '#4a5568',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  tr: {
    borderBottom: '1px solid #f0f0f0',
  },
  td: {
    padding: '12px',
    verticalAlign: 'middle',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  divisionCode: {
    background: '#e6fffa',
    color: '#234e52',
    padding: '4px 10px',
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: '600',
  },
  calAssignBadge: {
    background: '#ebf4ff',
    color: '#2b6cb0',
    padding: '4px 10px',
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: '600',
  },
  missingCalAssign: {
    color: '#c05621',
    fontSize: '13px',
  },
  colorCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  colorSwatch: {
    width: '24px',
    height: '24px',
    borderRadius: '4px',
    border: '1px solid #e2e8f0',
  },
  colorCode: {
    fontSize: '12px',
    color: '#718096',
  },
  showMoreBtn: {
    marginTop: '16px',
    padding: '8px 16px',
    background: 'transparent',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    color: '#4a5568',
    cursor: 'pointer',
    fontSize: '13px',
  },
  uploadSection: {
    textAlign: 'center',
    padding: '20px 0',
  },
  uploadBtn: {
    padding: '14px 32px',
    fontSize: '16px',
    fontWeight: '600',
    background: '#38a169',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  uploadBtnDisabled: {
    background: '#a0aec0',
    cursor: 'not-allowed',
  },
  statusMessage: {
    marginTop: '16px',
    padding: '12px 20px',
    borderRadius: '8px',
    fontSize: '14px',
  },
  successMessage: {
    background: '#f0fff4',
    color: '#276749',
    border: '1px solid #9ae6b4',
  },
  errorMessage: {
    background: '#fff5f5',
    color: '#c53030',
    border: '1px solid #feb2b2',
    marginTop: '12px',
    padding: '12px',
    borderRadius: '6px',
    fontSize: '14px',
  },
};

export default DivisionUploader;